/**
 * PatientAllergiesTab — Allergies inner tab of the patient Chart shell.
 *
 * This is a CONTAINER, not a view: it owns the read, the add/edit/delete state
 * and the soft-delete write, and delegates all rendering to the Signal DS
 * `AllergiesChartTab` organism. That split is the point — the DS organism is
 * presentational (`items` in, callbacks out), so the data layer stays here and
 * the table, empty state and row actions stop being a PA-local reimplementation.
 *
 * Renamed off `AllergiesChartTab` deliberately. It was never a shadow of the DS
 * component: same name, different layer. Keeping the name would have kept the
 * shadow-ledger row alive for a component that is not a duplicate of anything.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/allergies_tab.dart`.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ [Allergen chips row]                       [Add] (PR B)    │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ Allergen · Category · Criticality · Reaction · Severity ·  │
 *   │ Onset · Recorded · Recorder                                 │
 *   │ <rows>                                                      │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The chip strip up top reuses the canonical `AllergyChip` atom (Signal
 * DS — red ramp is locked for clinical danger). The table below holds
 * the full per-record detail.
 *
 * Tombstoned records (`isDeleted: true`) are filtered out client-side —
 * the wire returns them so an undo flow can resurrect them, but the
 * default read view hides them.
 *
 * FHIR: `AllergyIntolerance R4`. One row per resource. `code.text` →
 * allergen name; `reaction[0].manifestation` → reaction display;
 * `criticality` → high/low/unable-to-assess; `category` →
 * food/medication/environment/biologic.
 */

import { ChartSectionAdd } from './chart-section-header';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useMemo, useState } from 'react';
import {
  AllergiesChartTab,
  ModalYesNo,
  type AllergyItem,
  AllergyChip,
} from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { formatChartDate } from '@/shared/lib/date-format';
import { usePatientAllergies } from '@/patients/services/use-patient-allergies';
import { useUpdateAllergy } from '@/patients/services/use-update-allergy';
import {
  AddAllergyEditor,
  normalizeCoding,
  normalizePerformerDetails,
} from './add-allergy-dialog';
import type {
  PatientAllergy,
  UpdateAllergyBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

export interface PatientAllergiesTabProps {
  patientId: string | null | undefined;
}

export function PatientAllergiesTab({ patientId }: PatientAllergiesTabProps) {
  const { data, isLoading, isError, refetch } = usePatientAllergies(patientId);
  const [addOpen, setAddOpen] = useState(false);
  /** When non-null, the editor is open in EDIT mode for this allergy. */
  const [editing, setEditing] = useState<PatientAllergy | null>(null);
  /** When non-null, the delete confirm dialog is open for this allergy. */
  const [deleting, setDeleting] = useState<PatientAllergy | null>(null);
  const updateMut = useUpdateAllergy();
  // Strict equality on the tombstone flag — see `PatientAllergy.isDeleted`
  // doc-block. Treats only literal `true` as deleted; future-proofs against
  // the field being null / dropped by mistake.
  const allergies = (data ?? []).filter((a) => a.isDeleted !== true);

  // Open editor in EDIT mode; closes Add if it was open so only one
  // editor surface is mounted at a time (Flutter parity:
  // "Only one item can be edited at a time").
  const startEdit = (a: PatientAllergy) => {
    setAddOpen(false);
    setEditing(a);
  };

  const closeEditor = () => {
    setAddOpen(false);
    setEditing(null);
  };

  // Soft-delete via PUT /allergy/{id} with isDeleted: true. The body
  // round-trips every field from the existing record (curl-verified
  // 2026-05-19 — both Edit and Delete curls ship the full record).
  //
  // Strict round-trip: every field listed explicitly (no spreads).
  // Two reasons:
  //   1. If the GET response carries an extra field beyond `PatientAllergy`
  //      (e.g. server-side denormalization), the spread would leak it
  //      into the PUT body and the backend could reject or overwrite it.
  //   2. The `code` field on UPDATE is required to be plain `CodeWordCase`
  //      with NO nested `category` — listing only the 5 expected fields
  //      ensures any nested category from a previous CREATE doesn't
  //      ride along into the UPDATE wire (Samer PR #219 review §3+4).
  const confirmDelete = async () => {
    if (!deleting?.identifier || !patientId) return;
    const a = deleting;
    // Re-narrow after the early-return guard above — TS doesn't carry
    // the optional-chain narrowing through the alias.
    const identifier: string = a.identifier!;
    const recorder = a.recorder;
    if (!recorder) {
      // Same strict guard as the Edit path — don't silently replace
      // the recorder GUID on soft-delete.
      toast.error(
        "Cannot delete — this record is missing its original recorder. Please contact support.",
      );
      return;
    }
    const body: UpdateAllergyBody = {
      identifier,
      clinicalStatus: 'Active',
      type: 'allergy',
      // Normalize every Coding to the 5-field shape — see comment in
      // add-allergy-dialog.tsx `normalizeCoding()` for rationale.
      category: normalizeCoding(a.category),
      criticality: normalizeCoding(a.criticality),
      code: a.code
        ? {
            code: a.code.code ?? null,
            display: a.code.display ?? null,
            system: a.code.system ?? null,
            description: a.code.description ?? null,
            shortName: a.code.shortName ?? null,
          }
        : null,
      subject: a.subject ?? patientId,
      dataAbsentReason: 'n/a',
      encounter: a.encounter ?? null,
      onset: a.onset ?? null,
      onSetDisplay: a.onSetDisplay ?? null,
      recordedDate: a.recordedDate ?? null,
      recorder,
      asserter: a.asserter ?? recorder,
      note: a.note ?? '',
      reaction: {
        substance: normalizeCoding(a.reaction?.substance),
        onset: 'n/a',
        severity: 'n/a',
      },
      isDeleted: true,
      performerDetails: normalizePerformerDetails(a.performerDetails),
      createdDate: a.createdDate ?? null,
      // Flutter parity — speciality nests under performerDetails; the
      // top-level field ships `null` on UPDATE.
      speciality: null,
    };
    try {
      await updateMut.mutateAsync({
        patientId,
        allergyId: identifier,
        body,
      });
      toast.success(`Removed allergy: ${a.code?.display ?? 'Unknown'}`);
      setDeleting(null);
    } catch {
      /* Global error toast surfaces failure; keep the dialog open so
       * the user can retry without losing their delete intent. */
    }
  };

  // Stable key per record, reused as the DS item id so an action can find its
  // way back to the full PatientAllergy. Mirrors the key the old table used.
  const keyOf = (a: PatientAllergy, i: number) =>
    a.identifier ?? `${i}-${a.code?.code ?? 'unknown'}`;

  const items: AllergyItem[] = useMemo(
    () => allergies.map((a, i) => toAllergyItem(a, keyOf(a, i))),
    [allergies],
  );
  // The table shows the first 5 allergies, "Show all N" for the rest; the chip strip above it always names every one
  // (the at-a-glance danger signal is never cut). The index counts them all.
  const cap = useRowCap(items);
  useReportChartCount(isLoading ? null : allergies.length);
  const byId = useMemo(() => {
    const m = new Map<string, PatientAllergy>();
    allergies.forEach((a, i) => m.set(keyOf(a, i), a));
    return m;
  }, [allergies]);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record allergies."
          />
      </div>
    );
  }

  // The DS organism has no error state — it distinguishes loading from empty and
  // nothing else. An empty table would read as "no allergies", which is exactly
  // the false clinical reassurance a failed read must never give, so the error
  // case stays here, outside it.
  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load allergies."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
      </div>
    );
  }

  const editorOpen = addOpen || editing !== null;

  // Nothing recorded: the same one line and "Add allergy" as every other section (the DS organism's own empty
  // state is not drawn). The editor opens in its place.
  if (!isLoading && allergies.length === 0) {
    return (
      <div className="p-6 space-y-4">
        {editorOpen ? (
          <AddAllergyEditor key={editing?.identifier ?? 'add'} patientId={patientId} editing={editing} onClose={closeEditor} />
        ) : <EmptyState
            icon="clipboard"
            title="No allergies recorded for this patient."
            ctaLabel="Add allergy"
            onCtaClick={() => setAddOpen(true)}
          />}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <AllergiesChartTab
        items={cap.visible}
        loading={isLoading}
        // Deliberately NOT the DS default and never "NKDA": an empty list means
        // nothing has been recorded, not that the patient has no known drug
        // allergies. (PROD-1998 removed the old "non-drug only" line — the form
        // now records drug allergies too.)
        emptyTitle="No allergies recorded for this patient."
        emptyDescription="Record one with Add allergy."
        headerActions={
          editorOpen
            ? undefined
            : [
                <ChartSectionAdd key="add" label="Add allergy" onClick={() => setAddOpen(true)} />,
              ]
        }
        beforeTable={
          <div className="space-y-4">
            {/* Inline editor — Flutter parity (allergies_tab.dart:143-708):
             *  the form opens ABOVE the list, never in a dialog, and only one
             *  mode is mounted at a time ("Only one item can be edited at a
             *  time"). The `key` forces a remount when the target changes so
             *  RHF's defaultValues re-apply. */}
            {editorOpen ? (
              <AddAllergyEditor
                key={editing?.identifier ?? 'add'}
                patientId={patientId}
                editing={editing}
                onClose={closeEditor}
              />
            ) : null}
            {/* Chip strip — clinical-danger signal at a glance. */}
            {allergies.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allergies.map((a, i) => (
                  <AllergyChip
                    key={keyOf(a, i)}
                    allergen={a.code?.display ?? 'Unknown allergen'}
                    number={i + 1}
                  />
                ))}
              </div>
            ) : null}
          </div>
        }
        onEditItem={
          editorOpen
            ? undefined
            : (it) => {
                const a = byId.get(it.id);
                if (a) startEdit(a);
              }
        }
        onDeleteItem={
          editorOpen
            ? undefined
            : (it) => {
                const a = byId.get(it.id);
                if (a) setDeleting(a);
              }
        }
      />
      <ChartShowAll cap={cap} label="allergies" />

      {/* Delete confirmation — soft-delete via PUT with isDeleted: true.
       *  Mirrors Flutter `confirmDialog` (allergies_tab.dart:676-700)
       *  copy and check/cross semantics. */}
      <ModalYesNo
        open={deleting !== null}
        onCancel={() => setDeleting(null)}
        title="Are you sure?"
        message={
          <>
            Do you want to proceed on deleting{' '}
            <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
              {deleting?.code?.display ?? 'this allergy'}
            </span>
            ?
          </>
        }
        cancelLabel="Cancel"
        confirmLabel={updateMut.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => {
          void confirmDelete();
        }}
        loading={updateMut.isPending}
      />
    </div>
  );
}

/** FHIR R4 category value set, for recognising a canonical value. */
const CANONICAL_CATEGORY = ['food', 'medication', 'environment', 'biologic'];
/** FHIR R4 criticality value set. */
const CANONICAL_CRITICALITY = ['high', 'low', 'unable-to-assess'];

/**
 * Map one wire record onto the DS item shape.
 *
 * Category and criticality arrive as Codings whose contents are NOT guaranteed
 * to be the FHIR value set — the backend ships free text, and the previous table
 * rendered whatever it got. So: pass a canonical value canonically (the DS maps
 * its label and clinical tone), and otherwise pass the raw string through. The
 * DS renders unrecognised values verbatim on a neutral chip rather than dropping
 * them. Nothing is guessed into a canonical bucket here — inferring that "Drug"
 * means `medication` would be a clinical judgement, not a mapping.
 */
function toAllergyItem(a: PatientAllergy, id: string): AllergyItem {
  const rawCategory = (a.category?.display ?? a.category?.code ?? '').trim();
  const rawCriticality = (a.criticality?.display ?? a.criticality?.code ?? '').trim();
  const canon = (raw: string, set: string[]) =>
    set.includes(raw.toLowerCase()) ? raw.toLowerCase() : raw;

  return {
    id,
    allergen: a.code?.display ?? 'Unknown allergen',
    category: rawCategory ? canon(rawCategory, CANONICAL_CATEGORY) : undefined,
    reaction: a.reaction?.substance?.display ?? '—',
    // `reaction.severity` on the wire is encounter-side metadata (literally
    // "n/a"), never a clinical grade, so the DS Severity column stays empty
    // rather than being fed a placeholder. Criticality carries the signal.
    criticality: rawCriticality
      ? canon(rawCriticality, CANONICAL_CRITICALITY)
      : undefined,
    onset: formatDate(a.onset),
    recordedDate: formatDate(a.recordedDate),
    recorder: a.performerDetails?.name ?? undefined,
    notes: a.note?.trim() || undefined,
  };
}

/** "03 Sep 2025", the design's chart-table date (ChartRecordTable `date`), whatever the browser's locale. */
function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === 'n/a') return '—';
  return formatChartDate(iso) || '—';
}
